const CHROMIUM_OR_ALT_BROWSER_PATTERN =
  /Chrome|Chromium|CriOS|FxiOS|Edg|EdgiOS|OPR|OPiOS|Firefox/;
const IOS_BROWSER_PATTERN = /\b(CriOS|FxiOS|EdgiOS|OPiOS)\b/;

function getNavigator() {
  return typeof navigator !== 'undefined' ? navigator : null;
}

function getDefaultUserAgent() {
  return getNavigator()?.userAgent || '';
}

function isIPadOSDesktopMode() {
  const nav = getNavigator();
  return nav?.platform === 'MacIntel' && (nav?.maxTouchPoints ?? 0) > 1;
}

/**
 * Detects if the current browser is a FairPlay-capable browser (Safari/iOS)
 * @param {string} [userAgent] - Optional user agent string (defaults to navigator.userAgent)
 * @returns {boolean} True if the browser supports FairPlay DRM
 */
export function isFairPlayBrowser(userAgent) {
  const ua = userAgent ?? getDefaultUserAgent();
  return isIOS(userAgent) || IOS_BROWSER_PATTERN.test(ua) || isSafari(ua);
}

/**
 * Detects if the current browser is running on iOS
 * @param {string} [userAgent] - Optional user agent string (defaults to navigator.userAgent)
 * @returns {boolean} True if running on iOS
 */
export function isIOS(userAgent) {
  const ua = userAgent ?? getDefaultUserAgent();
  return /iPad|iPhone|iPod/.test(ua) ||
    (userAgent == null && isIPadOSDesktopMode());
}

/**
 * Detects phones and tablets, where software video decoding drains the battery
 * and is often too slow above SD. Used to require hardware decoding for
 * advanced codecs.
 * @param {string} [userAgent] - Optional user agent string (defaults to navigator.userAgent)
 * @returns {boolean} True on a mobile device
 */
export function isMobileDevice(userAgent) {
  const ua = userAgent ?? getDefaultUserAgent();
  return /Android|iPhone|iPad|iPod|Mobile/.test(ua) ||
    (userAgent == null && isIPadOSDesktopMode());
}

/**
 * Detects the WebKit engine, the only one that plays HLS natively.
 *
 * navigator.vendor rather than the user agent: a macOS WKWebView reports AppleWebKit but
 * usually omits the Safari token, so a UA test would miss it and force hls.js in an
 * engine that can do better. Chromium reports "Google Inc." and Gecko an empty string,
 * so both are excluded.
 * @param {string} [vendor] - Optional vendor string (defaults to navigator.vendor)
 * @returns {boolean} True if the engine is WebKit
 */
export function isWebKitEngine(vendor) {
  const value = vendor ?? globalThis.navigator?.vendor ?? '';
  return value === 'Apple Computer, Inc.' || isIOS();
}

/**
 * Detects if the current browser is Safari (but not Chrome)
 * @param {string} [userAgent] - Optional user agent string (defaults to navigator.userAgent)
 * @returns {boolean} True if the browser is Safari
 */
export function isSafari(userAgent) {
  const ua = userAgent ?? getDefaultUserAgent();
  return /Safari/.test(ua) && !CHROMIUM_OR_ALT_BROWSER_PATTERN.test(ua);
}

/**
 * Detects if the current browser uses the Gecko engine (Firefox, Waterfox, etc.)
 * @param {string} [userAgent] - Optional user agent string (defaults to navigator.userAgent)
 * @returns {boolean} True if the browser is Gecko-based
 */
export function isGecko(userAgent) {
  const ua = userAgent ?? getDefaultUserAgent();
  return /Gecko\//.test(ua) && !/like Gecko/.test(ua);
}

/**
 * Detects if the current browser is Chrome (or Chromium-based)
 * @param {string} [userAgent] - Optional user agent string (defaults to navigator.userAgent)
 * @returns {boolean} True if the browser is Chrome-based
 */
export function isChrome(userAgent) {
  const ua = userAgent ?? getDefaultUserAgent();
  return /Chrome|Chromium/.test(ua) && !/Edg|EdgiOS|OPR|OPiOS/.test(ua);
}

/**
 * Extracts the WebKit/Safari major version from the user agent.
 * Uses the "Version/NN" token when present (Safari, iPad desktop mode) and
 * falls back to the iOS OS version ("CPU iPhone OS 26_0" / "CPU OS 26_0")
 * for iOS browsers that omit the Version token (CriOS, FxiOS, etc.), since
 * every iOS browser ships the system WebKit.
 * @param {string} [userAgent] - Optional user agent string (defaults to navigator.userAgent)
 * @returns {number|null} Major version, or null if it cannot be determined
 */
export function getWebKitMajorVersion(userAgent) {
  const ua = userAgent ?? getDefaultUserAgent();
  const versionMatch = ua.match(/Version\/(\d+)/);
  if (versionMatch) return parseInt(versionMatch[1], 10);
  const iosMatch = ua.match(/(?:iPhone|iPad|iPod).+?\bOS (\d+)_/);
  if (iosMatch) return parseInt(iosMatch[1], 10);
  return null;
}
